import React, { useMemo, useState, useEffect, useContext, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TableContainer,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import IosShareIcon from "@mui/icons-material/IosShare";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
// Shared keyboard-first date/time field, aliased to the local name that
// used to be defined further down so no call site had to change.
import { StringDateTimeField as SingleDateTimePicker } from "../DateTimeField";
import { useNavigate } from "react-router-dom";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Autocomplete } from "@mui/material";
import { ReactComponent as ExcelExportIcon } from "../../assets/icons/export-as-excel.svg";
import excelIcon from "../../assets/icons/excel_export.png";

// =====================================================================
// Calendar / Time constants & helpers (all unchanged)
// =====================================================================
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({
      date: new Date(year, month - 1, prevMonthDays - i),
      outside: true,
    });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ date: new Date(year, month, d), outside: false });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++)
    cells.push({ date: new Date(year, month + 1, d), outside: true });
  return cells;
}

function toYMD(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromYMD(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function to12h(time24) {
  if (!time24) return { h12: "12", mm: "00", period: "AM" };
  const [rawH, rawM = "00"] = time24.split(":");
  let h = parseInt(rawH, 10);
  if (isNaN(h)) return { h12: "12", mm: "00", period: "AM" };
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return { h12: String(h), mm: rawM.padStart(2, "0"), period };
}

function to24h(h12, mm, period) {
  let h = parseInt(h12, 10);
  if (isNaN(h)) h = 12;
  if (period === "AM") {
    if (h === 12) h = 0;
  } else {
    if (h !== 12) h += 12;
  }
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// =====================================================================
// Helper: check if a driver is blocked on a specific date.
// =====================================================================
function isDriverBlockedOnDate(driver, checkDate) {
  if (!driver) return false;
  const blockedFrom = driver.blockedFrom || driver.blocked_from;
  const blockedTo = driver.blockedTo || driver.blocked_to;
  const status = driver.status;
  if (blockedFrom && blockedTo && checkDate) {
    const target = new Date(checkDate);
    const from = new Date(blockedFrom);
    const to = new Date(blockedTo);
    target.setHours(0, 0, 0, 0);
    from.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);
    return target >= from && target <= to;
  }
  if (status === "B" && !blockedFrom && !blockedTo) return true;
  return false;
}

// =====================================================================
// Improved datetime overlap check for bookings.
// =====================================================================
function bookingOccupiesDatetime(booking, checkDateStr, checkTimeStr) {
  const pickupDate = booking.trip_info?.pickup_date;
  const dropDate = booking.trip_info?.drop_date;
  const dropTime = booking.trip_info?.drop_time;
  const pickupTime = booking.trip_info?.pickup_time;
  if (!pickupDate) return false;
  if (!dropDate) return checkDateStr === pickupDate;
  if (!dropTime) return checkDateStr >= pickupDate && checkDateStr <= dropDate;
  const pickupDateTime = new Date(`${pickupDate}T${pickupTime || "00:00:00"}`);
  const dropDateTime = new Date(`${dropDate}T${dropTime}`);
  const checkTime = checkTimeStr || "00:00:00";
  const checkDateTime = new Date(`${checkDateStr}T${checkTime}`);
  return checkDateTime >= pickupDateTime && checkDateTime <= dropDateTime;
}

// =====================================================================
// Check if a driver is actively assigned to a DIFFERENT vehicle
// =====================================================================
function isDriverOnDifferentVehicle(
  driverId,
  defaultVehicleId,
  checkDate,
  allVehiclesData,
  checkTime,
) {
  if (!driverId || !checkDate) return false;
  const allBookings = allVehiclesData.flatMap((v) =>
    v.bookings.map((booking) => ({ ...booking, vehicle_id: v.vehicle_id })),
  );
  const conflictingBookings = allBookings.filter((booking) => {
    if (booking.vehicle_id === defaultVehicleId) return false;
    if (booking.driver_id !== driverId) return false;
    return bookingOccupiesDatetime(booking, checkDate, checkTime);
  });
  return conflictingBookings.length > 0;
}

// =====================================================================
// Check if a vehicle is actively booked on a given date/time
// =====================================================================
function isVehicleBookedOnDate(
  vehicleId,
  checkDate,
  currentBookingId,
  allVehiclesData,
  checkTime,
) {
  if (!vehicleId || !checkDate) return false;
  const vehicle = allVehiclesData.find((v) => v.vehicle_id === vehicleId);
  if (!vehicle) return false;
  const conflictingBookings = vehicle.bookings.filter((booking) => {
    if (currentBookingId && booking.booking_id === currentBookingId)
      return false;
    return bookingOccupiesDatetime(booking, checkDate, checkTime);
  });
  return conflictingBookings.length > 0;
}

// =====================================================================
// TimeSelector — FIXED: prevents picker from closing while using dropdowns
// =====================================================================
const TimeSelector = ({ time24, onChange, label, onSelectMouseDown }) => {
  const isUnset = !time24;
  const { h12, mm, period } = to12h(time24 || "");
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minutes = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0"),
  );

  const safeH = isUnset ? "12" : h12;
  const safeM = isUnset ? "00" : mm;
  const safeP = isUnset ? "AM" : period;

  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const periodRef = useRef(null);

  const selectSx = {
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "0.8rem",
    padding: "2px 4px",
    cursor: "pointer",
    outline: "none",
    background: "#f9f9f9",
    fontWeight: 600,
  };
  const placeholderSx = { ...selectSx, color: "#aaa" };

  const handleSelectMouseDown = (e) => {
    e.stopPropagation();
    if (onSelectMouseDown) onSelectMouseDown();
  };

  const handleKeyDown = (e, currentRef, nextRef) => {
    if (e.key === "Tab" && !e.shiftKey && nextRef) {
      e.preventDefault();
      nextRef.current?.focus();
    }
  };

  return (
    <Box sx={{ textAlign: "center", mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 0.5, fontWeight: 600, color: "#555" }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        <Box
          component="select"
          ref={hourRef}
          value={isUnset ? "" : h12}
          onChange={(e) => onChange(to24h(e.target.value, safeM, safeP))}
          onMouseDown={handleSelectMouseDown}
          onKeyDown={(e) => handleKeyDown(e, hourRef, minuteRef)}
          sx={isUnset ? placeholderSx : selectSx}
        >
          {isUnset && (
            <option value="" disabled>
              HH
            </option>
          )}
          {hours.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.9rem",
            color: isUnset ? "#aaa" : "inherit",
          }}
        >
          :
        </Typography>
        <Box
          component="select"
          ref={minuteRef}
          value={isUnset ? "" : mm}
          onChange={(e) => onChange(to24h(safeH, e.target.value, safeP))}
          onMouseDown={handleSelectMouseDown}
          onKeyDown={(e) => handleKeyDown(e, minuteRef, periodRef)}
          sx={isUnset ? placeholderSx : selectSx}
        >
          {isUnset && (
            <option value="" disabled>
              MM
            </option>
          )}
          {minutes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Box>
        <Box
          component="select"
          ref={periodRef}
          value={safeP}
          onChange={(e) => onChange(to24h(safeH, safeM, e.target.value))}
          onMouseDown={handleSelectMouseDown}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
          sx={isUnset ? placeholderSx : selectSx}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </Box>
      </Box>
    </Box>
  );
};

// =====================================================================
// Existing helpers (isoToDDMMYYYY, getDaysInMonth) unchanged
// =====================================================================
const isoToDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (isNaN(date)) return "";
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const getDaysInMonth = (monthYearStr) => {
  const [monthStr, yearStr] = monthYearStr.split(" ");
  const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
  const year = parseInt(yearStr);
  return new Date(year, monthIndex + 1, 0).getDate();
};

// =====================================================================
// AssignmentPanel — MOVED OUTSIDE DayWiseDashboard to prevent remounting
// which was causing the calendar to close on every interaction
// =====================================================================
const AssignmentPanel = ({
  isVehicleOtherMode,
  tempVehicleAssign,
  setTempVehicleAssign,
  setIsVehicleOtherMode,
  setSelectedVehicleId,
  setAssignVehicleObj,
  availableVehicles,
  assignVehicleObj,
  handleVehicleChange,
  isVehicleBooked,
  isDriverOtherMode,
  tempDriverAssign,
  setTempDriverAssign,
  setIsDriverOtherMode,
  setSelectedDriverId,
  setAssignDriverObj,
  availableDrivers,
  assignDriverObj,
  handleDriverChange,
  isDriverBooked,
  startDate,
  startTime,
  setStartDate,
  setStartTime,
  endDate,
  endTime,
  setEndDate,
  setEndTime,
  handleUpdateAssignment,
  isUpdatingAssignment,
  dateFieldKey,
}) => (
  <Card variant="outlined" sx={{ bgcolor: "#f8f9fa" }}>
    <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={1}
        sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" }, fontWeight: 600 }}
      >
        Update Assignment
      </Typography>

      {/* VEHICLE */}
      {isVehicleOtherMode ? (
        <TextField
          size="small"
          fullWidth
          label="Vehicle (Other)"
          placeholder="Enter vehicle name / number"
          value={tempVehicleAssign}
          onChange={(e) => setTempVehicleAssign(e.target.value)}
          autoFocus
          sx={{ mb: 1.5 }}
          InputProps={{
            endAdornment: (
              <IconButton
                size="small"
                onClick={() => {
                  setIsVehicleOtherMode(false);
                  setTempVehicleAssign("");
                  setSelectedVehicleId("");
                  setAssignVehicleObj(null);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
      ) : (
        <Autocomplete
          size="small"
          options={[
            {
              vehicle_id: "other",
              vehicle_code: "Other",
              vehicle_name: "Other (Enter Manually)",
            },
            ...availableVehicles,
          ]}
          value={assignVehicleObj}
          onChange={(_, newValue) => {
            if (newValue?.vehicle_id === "other") {
              setIsVehicleOtherMode(true);
              setTempVehicleAssign("");
              setSelectedVehicleId("");
              setAssignVehicleObj(null);
            } else {
              handleVehicleChange(newValue?.vehicle_id || "", newValue);
            }
          }}
          getOptionLabel={(option) =>
            option.vehicle_id === "other"
              ? "Other (Enter Manually)"
              : `${option.vehicle_code || ""} - ${option.vehicle_name || ""}`
          }
          isOptionEqualToValue={(option, value) =>
            option.vehicle_id === value?.vehicle_id
          }
          renderOption={(props, option) => {
            const vehicleBooked = isVehicleBooked(option.vehicle_id);
            return (
              <li
                {...props}
                style={{
                  ...props.style,
                  backgroundColor: vehicleBooked ? "#ffebee" : "transparent",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <span>
                    {option.vehicle_id === "other"
                      ? "Other (Enter Manually)"
                      : `${option.vehicle_code || ""} - ${
                          option.vehicle_name || ""
                        }`}
                  </span>
                  {vehicleBooked && (
                    <Chip
                      label="Booked"
                      size="small"
                      color="error"
                      sx={{ fontSize: "0.6rem", height: 16, ml: "auto" }}
                    />
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vehicle"
              sx={{
                mb: 1.5,
                "& .MuiInputBase-input": { fontSize: "0.8rem" },
              }}
            />
          )}
        />
      )}

      {/* DRIVER */}
      {isDriverOtherMode ? (
        <TextField
          size="small"
          fullWidth
          label="Driver (Other)"
          placeholder="Enter driver name & mobile"
          value={tempDriverAssign}
          onChange={(e) => setTempDriverAssign(e.target.value)}
          autoFocus
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <IconButton
                size="small"
                onClick={() => {
                  setIsDriverOtherMode(false);
                  setTempDriverAssign("");
                  setSelectedDriverId("");
                  setAssignDriverObj(null);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
      ) : (
        <Autocomplete
          size="small"
          options={[
            {
              id: "other",
              driver_code: "Other",
              name: "Other (Enter Manually)",
            },
            ...availableDrivers,
          ]}
          value={assignDriverObj}
          onChange={(_, newValue) => {
            if (newValue?.id === "other") {
              setIsDriverOtherMode(true);
              setTempDriverAssign("");
              setSelectedDriverId("");
              setAssignDriverObj(null);
            } else {
              handleDriverChange(newValue?.id || "", newValue);
            }
          }}
          getOptionLabel={(option) =>
            option.id === "other"
              ? "Other (Enter Manually)"
              : `${option.code || option.driver_code || ""} - ${
                  option.name || ""
                }`
          }
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderOption={(props, option) => {
            const driverBooked = isDriverBooked(option.id);
            return (
              <li
                {...props}
                style={{
                  ...props.style,
                  backgroundColor: driverBooked ? "#ffebee" : "transparent",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <span>
                    {option.id === "other"
                      ? "Other (Enter Manually)"
                      : `${option.code || option.driver_code || ""} - ${
                          option.name || ""
                        }`}
                  </span>
                  {driverBooked && (
                    <Chip
                      label="Booked"
                      size="small"
                      color="error"
                      sx={{ fontSize: "0.6rem", height: 16, ml: "auto" }}
                    />
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Driver"
              sx={{ mb: 1, "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
            />
          )}
        />
      )}

      {/* Start Date & Time */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={0.5}
          sx={{ fontSize: "0.75rem" }}
        >
          Start Date &amp; Time
        </Typography>
        <SingleDateTimePicker
          key={`start-${dateFieldKey}`}
          label="Start"
          date={startDate}
          time={startTime}
          onDateChange={(val) => setStartDate(val)}
          onTimeChange={(val) => setStartTime(val)}
        />
      </Box>

      {/* End Date & Time */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={0.5}
          sx={{ fontSize: "0.75rem" }}
        >
          End Date &amp; Time
        </Typography>
        <SingleDateTimePicker
          key={`end-${dateFieldKey}`}
          label="End"
          date={endDate}
          time={endTime}
          onDateChange={(val) => setEndDate(val)}
          onTimeChange={(val) => setEndTime(val)}
          minDate={startDate || null}
        />
      </Box>

      <Button
        onClick={handleUpdateAssignment}
        variant="contained"
        size="small"
        fullWidth
        disabled={isUpdatingAssignment}
        startIcon={
          isUpdatingAssignment ? <CircularProgress size={16} /> : <SaveIcon />
        }
        sx={{ fontSize: "0.75rem" }}
      >
        {isUpdatingAssignment ? "Updating..." : "Update Assignment"}
      </Button>
    </CardContent>
  </Card>
);

// =====================================================================
// Main Component
// =====================================================================
export default function DayWiseDashboard({
  selectedMonth,
  selectedDate,
  setSelectedDate,
  activeFilter,
  vehiclesData,
  onRefresh,
  onNavigateToBooking,
  setSelectedMonth,
}) {
  const { userInfo, templateData } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [allVehicles, setAllVehicles] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");

  const [isVehicleOtherMode, setIsVehicleOtherMode] = useState(false);
  const [isDriverOtherMode, setIsDriverOtherMode] = useState(false);
  const [tempVehicleAssign, setTempVehicleAssign] = useState("");
  const [tempDriverAssign, setTempDriverAssign] = useState("");
  const [assignVehicleObj, setAssignVehicleObj] = useState(null);
  const [assignDriverObj, setAssignDriverObj] = useState(null);
  const [isUpdatingAssignment, setIsUpdatingAssignment] = useState(false);
  const [tabAvailableVehicles, setTabAvailableVehicles] = useState([]);
  const [isFetchingTabAvailable, setIsFetchingTabAvailable] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");

  const [isWhatsappDialogOpen, setIsWhatsappDialogOpen] = useState(false);
  const [waText, setWaText] = useState("");
  const [isSendingWA, setIsSendingWA] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentTemplateName, setCurrentTemplateName] = useState("");
  const [currentRecipientPhone, setCurrentRecipientPhone] = useState("");
  const [currentRecipientName, setCurrentRecipientName] = useState("");
  const [currentBookingId, setCurrentBookingId] = useState(null);

  // ---- IMPROVED confirm dialog state: carries conflict detail message ----
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [pendingSelectionType, setPendingSelectionType] = useState(""); // "driver" or "vehicle"
  const [conflictMessage, setConflictMessage] = useState(""); // human-readable conflict info

  // ---- NEW: Expand/collapse state for Trip Details ----
  const [expandedTripRows, setExpandedTripRows] = useState({});

  const toggleTripExpand = (bookingId) => {
    setExpandedTripRows((prev) => ({
      ...prev,
      [bookingId]: !prev[bookingId],
    }));
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();
  const loggedUser = JSON.parse(localStorage.getItem("CapacitorStorage.user"));
  const user_id = loggedUser.id;
  const user_mobile = loggedUser.mobile;

  const extendedDays = useMemo(() => {
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);

    const days = [];

    // 5 days from previous month
    const prevMonthLastDay = new Date(year, monthIndex, 0).getDate();
    for (let i = 4; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const prevMonth = monthIndex === 0 ? 11 : monthIndex - 1;
      const prevYear = monthIndex === 0 ? year - 1 : year;
      const monthShort = new Date(prevYear, prevMonth, 1).toLocaleString(
        "default",
        { month: "short" },
      );
      days.push({
        day,
        label: `${monthShort} ${prevYear}`,
        isCurrentMonth: false,
        isPrev: true,
      });
    }

    // Current month days
    const numDays = new Date(year, monthIndex + 1, 0).getDate();
    const monthShort = new Date(year, monthIndex, 1).toLocaleString("default", {
      month: "short",
    });
    for (let d = 1; d <= numDays; d++) {
      days.push({
        day: d,
        label: `${monthShort} ${year}`,
        isCurrentMonth: true,
        isPrev: false,
      });
    }

    // 5 days from next month
    const nextMonth = monthIndex === 11 ? 0 : monthIndex + 1;
    const nextYear = monthIndex === 11 ? year + 1 : year;
    const nextMonthShort = new Date(nextYear, nextMonth, 1).toLocaleString(
      "default",
      { month: "short" },
    );
    for (let d = 1; d <= 5; d++) {
      days.push({
        day: d,
        label: `${nextMonthShort} ${nextYear}`,
        isCurrentMonth: false,
        isPrev: false,
      });
    }

    return days;
  }, [selectedMonth]);

  useEffect(() => {
    if (!store_id) return;
    const fetchVehicles = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/vehicles/store/${store_id}`;
        const response = await axios.get(url);
        if (response?.data?.success) setAllVehicles(response.data.data);
      } catch (error) {
        console.error("Fetch vehicles error:", error);
      }
    };
    const fetchDrivers = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/drivers/store/${store_id}`;
        const response = await axios.get(url);
        if (response?.data?.success) setAllDrivers(response.data.data);
      } catch (error) {
        console.error("Fetch drivers error:", error);
      }
    };
    fetchVehicles();
    fetchDrivers();
  }, [store_id]);

  useEffect(() => {
    if (activeFilter !== "AVAILABLE" || !store_id || !selectedDate) return;
    const fetchTabAvailableVehicles = async () => {
      setIsFetchingTabAvailable(true);
      try {
        const [monthStr, yearStr] = selectedMonth.split(" ");
        const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
        const year = parseInt(yearStr);
        const dateStr = `${year}-${String(monthIndex + 1).padStart(
          2,
          "0",
        )}-${String(selectedDate).padStart(2, "0")}`;
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/vehicles/available/${store_id}?date=${dateStr}`;
        const response = await axios.get(url);
        if (response?.data?.success)
          setTabAvailableVehicles(response.data.data);
      } catch (error) {
        console.error("Fetch tab available vehicles error:", error);
        setSnackbar({
          open: true,
          message: "Failed to fetch available vehicles",
          severity: "error",
        });
      } finally {
        setIsFetchingTabAvailable(false);
      }
    };
    fetchTabAvailableVehicles();
  }, [activeFilter, store_id, selectedDate, selectedMonth]);

  const selectedBooking = selectedVehicle?.bookingsForDate?.find(
    (b) => b.booking_id === selectedBookingId,
  );

  const currentEditingBookingId = useMemo(() => {
    const b = selectedBooking || selectedVehicle?.booking || null;
    return b?.booking_id || null;
  }, [selectedBooking, selectedVehicle]);

  const newTripRange = useMemo(() => {
    if (!startDate) return null;
    const pickup = new Date(`${startDate}T${startTime || "00:00:00"}`);
    let drop;
    if (endDate && endTime) {
      drop = new Date(`${endDate}T${endTime}`);
    } else if (endDate) {
      drop = new Date(`${endDate}T23:59:59`);
    } else {
      drop = new Date(`${startDate}T23:59:59`);
    }
    return { pickup, drop };
  }, [startDate, startTime, endDate, endTime]);

  const overlapsNewTrip = (b, range) => {
    const bPickup = b.trip_info?.pickup_date;
    const bDrop = b.trip_info?.drop_date;
    const bPickupTime = b.trip_info?.pickup_time;
    const bDropTime = b.trip_info?.drop_time;
    if (!bPickup || !range) return false;
    const existPickup = new Date(`${bPickup}T${bPickupTime || "00:00:00"}`);
    let existDrop;
    if (bDrop && bDropTime) {
      existDrop = new Date(`${bDrop}T${bDropTime}`);
    } else if (bDrop) {
      existDrop = new Date(`${bDrop}T23:59:59`);
    } else {
      existDrop = new Date(`${bPickup}T23:59:59`);
    }
    return range.pickup < existDrop && range.drop > existPickup;
  };

  // ---- Returns the conflicting booking object (not just boolean) ----
  const getDriverConflictBooking = (driverId) => {
    if (!startDate || !newTripRange) return null;
    const allBookings = vehiclesData.flatMap((v) => v.bookings);
    return (
      allBookings.find((b) => {
        if (b.driver_id !== driverId) return false;
        if (currentEditingBookingId && b.booking_id === currentEditingBookingId)
          return false;
        return overlapsNewTrip(b, newTripRange);
      }) || null
    );
  };

  const getVehicleConflictBooking = (vehicleId) => {
    if (!startDate || !newTripRange) return null;
    const vehicleData = vehiclesData.find((v) => v.vehicle_id === vehicleId);
    if (!vehicleData) return null;
    return (
      vehicleData.bookings.find((b) => {
        if (currentEditingBookingId && b.booking_id === currentEditingBookingId)
          return false;
        return overlapsNewTrip(b, newTripRange);
      }) || null
    );
  };

  const isDriverBooked = (driverId) => !!getDriverConflictBooking(driverId);
  const isVehicleBooked = (vehicleId) => !!getVehicleConflictBooking(vehicleId);

  // Build a human-readable conflict description from a booking object
  const buildConflictDescription = (conflictingBooking, type) => {
    if (!conflictingBooking) return "";
    const pickup = conflictingBooking.trip_info?.pickup_date;
    const pickupTime = conflictingBooking.trip_info?.pickup_time;
    const drop = conflictingBooking.trip_info?.drop_date;
    const dropTime = conflictingBooking.trip_info?.drop_time;
    const customer = conflictingBooking.customer_name || "Unknown customer";
    const bookingId = conflictingBooking.booking_id || "";

    const formatDT = (date, time) => {
      if (!date) return "N/A";
      const [y, m, d] = date.split("-");
      const datePart = `${d}-${m}-${y}`;
      if (!time) return datePart;
      const { h12, mm, period } = to12h(time);
      return `${datePart} ${h12}:${mm} ${period}`;
    };

    const from = formatDT(pickup, pickupTime);
    const to = drop ? formatDT(drop, dropTime) : formatDT(pickup, pickupTime);

    return `Booking #${bookingId} for "${customer}" from ${from} to ${to}`;
  };

  // All drivers EXCEPT permanently blocked/inactive ones
  const availableDrivers = useMemo(() => {
    return allDrivers.filter((driver) => {
      if (driver.status === "I") return false;
      if (driver.status === "B") {
        const blockedFrom = driver.blockedFrom || driver.blocked_from;
        const blockedTo = driver.blockedTo || driver.blocked_to;
        if (!blockedFrom || !blockedTo) return false;
        if (startDate) {
          const tripStart = new Date(startDate);
          const tripEnd = endDate ? new Date(endDate) : new Date(startDate);
          const bFrom = new Date(blockedFrom);
          const bTo = new Date(blockedTo);
          tripStart.setHours(0, 0, 0, 0);
          tripEnd.setHours(0, 0, 0, 0);
          bFrom.setHours(0, 0, 0, 0);
          bTo.setHours(0, 0, 0, 0);
          if (tripStart <= bTo && tripEnd >= bFrom) return false;
        }
      } else if (driver.status !== "A") {
        return false;
      }
      return true;
    });
  }, [allDrivers, startDate, endDate]);

  // All vehicles EXCEPT permanently blocked ones
  const availableVehicles = useMemo(() => {
    return allVehicles.filter((vehicle) => {
      if (vehicle.blocked_from && vehicle.blocked_to) {
        const blockedFrom = new Date(vehicle.blocked_from);
        const blockedTo = new Date(vehicle.blocked_to);
        blockedFrom.setHours(0, 0, 0, 0);
        blockedTo.setHours(0, 0, 0, 0);
        if (startDate) {
          const newPickupDay = new Date(startDate);
          newPickupDay.setHours(0, 0, 0, 0);
          const newDropDay = endDate ? new Date(endDate) : new Date(startDate);
          newDropDay.setHours(0, 0, 0, 0);
          if (newPickupDay <= blockedTo && newDropDay >= blockedFrom)
            return false;
        }
      }
      return true;
    });
  }, [allVehicles, startDate, endDate]);

  const resetAssignmentFields = () => {
    setSelectedDriverId("");
    setSelectedVehicleId("");
    setAssignDriverObj(null);
    setAssignVehicleObj(null);
    setTempDriverAssign("");
    setTempVehicleAssign("");
    setIsDriverOtherMode(false);
    setIsVehicleOtherMode(false);
    setStartDate("");
    setStartTime("");
    setEndDate("");
    setEndTime("");
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    const [hours, minutes] = time.split(":");
    const hr = parseInt(hours);
    const ampm = hr >= 12 ? "PM" : "AM";
    const displayHr = hr % 12 || 12;
    return `${displayHr}:${minutes} ${ampm}`;
  };

  const formatDateTime24 = (date, time) => {
    if (!date) return "";
    const d = new Date(`${date}T${time || "00:00"}`);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}-${month} ${hours}:${minutes} ${ampm}`;
  };

  const handleRowClick = (vehicleRow) => {
    setSelectedVehicle(vehicleRow);
    if (vehicleRow.bookingsForDate && vehicleRow.bookingsForDate.length === 1) {
      const booking = vehicleRow.bookingsForDate[0];
      setSelectedBookingId(booking.booking_id);
      setSelectedDriverId(booking.driver_id || "");
      setSelectedVehicleId(vehicleRow.vehicle_id || "");
      setStartDate(booking.trip_info?.pickup_date || "");
      setStartTime(booking.trip_info?.pickup_time || "");
      setEndDate(booking.trip_info?.drop_date || "");
      setEndTime(booking.trip_info?.drop_time || "");
      setAssignVehicleObj(
        allVehicles.find((v) => v.vehicle_id === vehicleRow.vehicle_id) || null,
      );
      const driverObj = allDrivers.find((d) => d.id === booking.driver_id);
      if (driverObj && driverObj.status !== "I") {
        setAssignDriverObj(driverObj);
      } else {
        setAssignDriverObj(null);
        setSelectedDriverId("");
      }
      setTempVehicleAssign("");
      setTempDriverAssign("");
      setIsVehicleOtherMode(false);
      setIsDriverOtherMode(false);
    } else {
      resetAssignmentFields();
    }
  };

  const handleBookingSelect = (bookingId) => {
    setSelectedBookingId(bookingId);
    const booking = selectedVehicle.bookingsForDate.find(
      (b) => b.booking_id === bookingId,
    );
    if (booking) {
      setSelectedDriverId(booking.driver_id || "");
      setSelectedVehicleId(booking.vehicle_id || "");
      setStartDate(booking.trip_info?.pickup_date || "");
      setStartTime(booking.trip_info?.pickup_time || "");
      setEndDate(booking.trip_info?.drop_date || "");
      setEndTime(booking.trip_info?.drop_time || "");
      setAssignVehicleObj(
        allVehicles.find((v) => v.vehicle_id === booking.vehicle_id) || null,
      );
      const driverObj = allDrivers.find((d) => d.id === booking.driver_id);
      if (driverObj && driverObj.status === "A") {
        setAssignDriverObj(driverObj);
      } else {
        setAssignDriverObj(null);
        setSelectedDriverId("");
      }
      setTempVehicleAssign("");
      setTempDriverAssign("");
      setIsVehicleOtherMode(false);
      setIsDriverOtherMode(false);
    }
  };

  const handleDeleteClick = (e, booking) => {
    e.stopPropagation();
    setBookingToDelete(booking);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) return;
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/travel/booking/${bookingToDelete.booking_id}`,
      );
      if (response.data.success) {
        onRefresh();
        setDeleteDialogOpen(false);
        setBookingToDelete(null);
        setSelectedVehicle(null);
        setSelectedBookingId(null);
        resetAssignmentFields();
      } else {
        setSnackbar({
          open: true,
          message: response.data.statusMessage || "Failed to delete booking",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Network error. Failed to delete booking.",
        severity: "error",
      });
      console.error("Delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const checkBookingConflict = (
    driverId,
    vehicleId,
    currentBookingId,
    pickupDate,
    pickupTime,
    dropDate,
    dropTime,
  ) => {
    const allBookings = vehiclesData.flatMap((v) => v.bookings);
    const otherBookings = allBookings.filter(
      (b) => b.booking_id !== currentBookingId,
    );
    const parseDateTime = (date, time) => {
      if (!date || !time) return null;
      return new Date(`${date}T${time}`);
    };
    const newPickup = parseDateTime(pickupDate, pickupTime);
    const newDrop = parseDateTime(dropDate, dropTime);
    for (const booking of otherBookings) {
      if (booking.driver_id !== driverId && booking.vehicle_id !== vehicleId)
        continue;
      const existingPickup = parseDateTime(
        booking.trip_info?.pickup_date,
        booking.trip_info?.pickup_time,
      );
      const existingDrop = parseDateTime(
        booking.trip_info?.drop_date,
        booking.trip_info?.drop_time,
      );
      if (!existingPickup) continue;
      const effectiveNewDrop = newDrop || newPickup;
      const effectiveExistingDrop = existingDrop || existingPickup;
      const hasOverlap =
        newPickup <= effectiveExistingDrop &&
        effectiveNewDrop >= existingPickup;
      if (hasOverlap) {
        const conflictType =
          booking.driver_id === driverId ? "Driver" : "Vehicle";
        return {
          hasConflict: true,
          message: `${conflictType} is already booked from ${formatDateTime24(
            booking.trip_info?.pickup_date,
            booking.trip_info?.pickup_time,
          )} to ${formatDateTime24(
            booking.trip_info?.drop_date || booking.trip_info?.pickup_date,
            booking.trip_info?.drop_time || booking.trip_info?.pickup_time,
          )}`,
        };
      }
    }
    return { hasConflict: false };
  };

  // ---- UPDATED: show conflict detail in dialog, then proceed on Yes ----
  const handleDriverChange = (driverId, driverObj) => {
    const conflictBooking = getDriverConflictBooking(driverId);
    if (conflictBooking) {
      const msg = buildConflictDescription(conflictBooking, "driver");
      setConflictMessage(msg);
      setPendingSelection({ id: driverId, obj: driverObj });
      setPendingSelectionType("driver");
      setConfirmDialogOpen(true);
    } else {
      proceedWithDriverSelection(driverId, driverObj);
    }
  };

  const proceedWithDriverSelection = (driverId, driverObj) => {
    setSelectedDriverId(driverId);
    setAssignDriverObj(driverObj);

    const noVehicleSelected =
      !selectedVehicleId ||
      selectedVehicleId === "" ||
      Number(selectedVehicleId) >= 99999;

    if (
      noVehicleSelected &&
      driverObj &&
      driverObj.vehicle_id &&
      Number(driverObj.vehicle_id) < 99999 &&
      (driverObj.status === "A" || driverObj.status === "B")
    ) {
      const isDefaultVehicleFree = availableVehicles.some(
        (v) => v.vehicle_id === driverObj.vehicle_id,
      );
      if (isDefaultVehicleFree && !isVehicleBooked(driverObj.vehicle_id)) {
        setSelectedVehicleId(driverObj.vehicle_id);
        setAssignVehicleObj(
          allVehicles.find((v) => v.vehicle_id === driverObj.vehicle_id) ||
            null,
        );
      }
    }
  };

  // ---- UPDATED: show conflict detail in dialog, then proceed on Yes ----
  const handleVehicleChange = (vehicleId, vehicleObj) => {
    const conflictBooking = getVehicleConflictBooking(vehicleId);
    if (conflictBooking) {
      const msg = buildConflictDescription(conflictBooking, "vehicle");
      setConflictMessage(msg);
      setPendingSelection({ id: vehicleId, obj: vehicleObj });
      setPendingSelectionType("vehicle");
      setConfirmDialogOpen(true);
    } else {
      proceedWithVehicleSelection(vehicleId, vehicleObj);
    }
  };

  const proceedWithVehicleSelection = (vehicleId, vehicleObj) => {
    setSelectedVehicleId(vehicleId);
    setAssignVehicleObj(vehicleObj);

    if (!selectedDriverId) {
      const driver = allDrivers.find((d) => d.vehicle_id === vehicleId);
      if (driver) {
        if (driver.status === "I") return;
        if (driver.status === "B") {
          const blockedFrom = driver.blockedFrom || driver.blocked_from;
          const blockedTo = driver.blockedTo || driver.blocked_to;
          if (!blockedFrom || !blockedTo) return;
          if (startDate) {
            const tripStart = new Date(startDate);
            const tripEnd = endDate ? new Date(endDate) : new Date(startDate);
            const bFrom = new Date(blockedFrom);
            const bTo = new Date(blockedTo);
            tripStart.setHours(0, 0, 0, 0);
            tripEnd.setHours(0, 0, 0, 0);
            bFrom.setHours(0, 0, 0, 0);
            bTo.setHours(0, 0, 0, 0);
            if (tripStart <= bTo && tripEnd >= bFrom) return;
          }
        } else if (driver.status !== "A") {
          return;
        }
        const isDefaultDriverFree = availableDrivers.some(
          (d) => d.id === driver.id,
        );
        if (isDefaultDriverFree && !isDriverBooked(driver.id)) {
          setSelectedDriverId(driver.id);
          setAssignDriverObj(driver);
        }
      }
    }
  };

  const handleConfirmDialogYes = () => {
    if (pendingSelectionType === "driver") {
      proceedWithDriverSelection(pendingSelection.id, pendingSelection.obj);
    } else if (pendingSelectionType === "vehicle") {
      proceedWithVehicleSelection(pendingSelection.id, pendingSelection.obj);
    }
    setConfirmDialogOpen(false);
    setPendingSelection(null);
    setPendingSelectionType("");
    setConflictMessage("");
  };

  const handleConfirmDialogNo = () => {
    setConfirmDialogOpen(false);
    setPendingSelection(null);
    setPendingSelectionType("");
    setConflictMessage("");
  };

  const handleUpdateAssignment = async () => {
    const hasDriver =
      (selectedDriverId && selectedDriverId !== "") ||
      (isDriverOtherMode && tempDriverAssign.trim());
    const hasVehicle =
      (selectedVehicleId && selectedVehicleId !== "") ||
      (isVehicleOtherMode && tempVehicleAssign.trim());

    // if (!hasDriver || !hasVehicle) {
    //   setSnackbar({
    //     open: true,
    //     message:
    //       "Please select both driver and vehicle (or enter them manually)",
    //     severity: "error",
    //   });
    //   return;
    // }

    const currentBooking = selectedBooking || selectedVehicle?.booking;
    if (!currentBooking) {
      setSnackbar({
        open: true,
        message: "No booking selected",
        severity: "error",
      });
      return;
    }

    if (!isDriverOtherMode && !isVehicleOtherMode) {
      const conflictCheck = checkBookingConflict(
        selectedDriverId,
        selectedVehicleId,
        currentBooking.booking_id,
        startDate,
        startTime,
        endDate,
        endTime,
      );
      // if (conflictCheck.hasConflict) {
      //   setSnackbar({
      //     open: true,
      //     message: conflictCheck.message,
      //     severity: "error",
      //   });
      //   return;
      // }
    }

    const checkValidVehcleId = isVehicleOtherMode
      ? tempVehicleAssign.trim()
      : selectedVehicleId || null;
    const effectiveDriverId = isDriverOtherMode
      ? tempDriverAssign.trim()
      : selectedDriverId || null;

    const tempVehId = checkValidVehcleId + "";
    const effectiveVehicleId = tempVehId.includes("999")
      ? null
      : checkValidVehcleId;

    setIsUpdatingAssignment(true);
    try {
      const payload = {
        store_id,
        created_by: user_id,
        customer_id: currentBooking.customer_id,
        booking_type: currentBooking.type.includes("B") ? "B" : "E",
        customer_name: currentBooking.customer_name,
        customer_mobile: currentBooking.customer_mobile,
        trip_details: currentBooking.trip_details,
        pickup_date: startDate,
        pickup_time: startTime,
        pax: currentBooking.trip_info?.pax,
        pickup_location: currentBooking.trip_info?.pickup_location,
        pickup_address: currentBooking.trip_info?.pickup_address,
        drop_location: currentBooking.trip_info?.drop_location,
        drop_address: currentBooking.trip_info?.drop_address,
        drop_date: endDate || null,
        drop_time: endTime || null,
        trip_mode: currentBooking.trip_info?.mode,
        quote: parseFloat(currentBooking.amount) || 0,
        advance: parseFloat(currentBooking.advance) || 0,
        vehicle_id: effectiveVehicleId,
        driver_id: effectiveDriverId,
        confirmation: currentBooking.confirmation,
        payment_details: currentBooking.trip_info?.payment_details,
        instructions: currentBooking.instructions,
        vehicle_type: currentBooking.trip_info?.vehicle_type,
        isVehicleOther: isVehicleOtherMode,
        isDriverOther: isDriverOtherMode,
        stage: currentBooking.stage || "active",
      };

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/booking/${currentBooking.booking_id}`;
      const response = await axios.put(url, payload);

      if (response.data.success) {
        setSnackbar({
          open: true,
          message:
            response.data.statusMessage || "Assignment updated successfully!",
          severity: "success",
        });
        await onRefresh();
      } else {
        setSnackbar({
          open: true,
          message: response.data.statusMessage || "Failed to update assignment",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Update assignment error:", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.statusMessage ||
          error?.response?.data?.message ||
          "Error updating assignment",
        severity: "error",
      });
    } finally {
      setIsUpdatingAssignment(false);
    }
  };

  const isVehicleBlocked = (vehicle, selectedDateStr) => {
    if (!vehicle.is_blocked) return false;
    if (!vehicle.blocked_from || !vehicle.blocked_to) return false;
    const selectedDate = new Date(selectedDateStr);
    const blockedFrom = new Date(vehicle.blocked_from);
    const blockedTo = new Date(vehicle.blocked_to);
    selectedDate.setHours(0, 0, 0, 0);
    blockedFrom.setHours(0, 0, 0, 0);
    blockedTo.setHours(0, 0, 0, 0);
    return selectedDate >= blockedFrom && selectedDate <= blockedTo;
  };

  const processedVehicles = useMemo(() => {
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);
    const targetDateStr = `${year}-${String(monthIndex + 1).padStart(
      2,
      "0",
    )}-${String(selectedDate).padStart(2, "0")}`;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(year, monthIndex, selectedDate);
    const isPastDate = targetDate < today;
    const result = [];

    const driverBookingsOnOtherVehicles = {};
    vehiclesData.forEach((v) => {
      v.bookings.forEach((b) => {
        if (!b.driver_id) return;
        const pickup = b.trip_info?.pickup_date;
        const drop = b.trip_info?.drop_date;
        if (!pickup) return;
        const occupiesDate = drop
          ? targetDateStr >= pickup && targetDateStr <= drop
          : targetDateStr === pickup;
        if (!occupiesDate) return;
        if (!driverBookingsOnOtherVehicles[b.driver_id]) {
          driverBookingsOnOtherVehicles[b.driver_id] = [];
        }
        driverBookingsOnOtherVehicles[b.driver_id].push({
          vehicle_id: v.vehicle_id,
          booking_id: b.booking_id,
        });
      });
    });

    vehiclesData.forEach((vehicle) => {
      const bookingsForDate = vehicle.bookings.filter((booking) => {
        const pickupDate = booking.trip_info?.pickup_date;
        const dropDate = booking.trip_info?.drop_date;
        if (!pickupDate) return false;
        if (!dropDate || dropDate === null || dropDate === "")
          return pickupDate === targetDateStr;
        return targetDateStr >= pickupDate && targetDateStr <= dropDate;
      });

      const blocked = isVehicleBlocked(vehicle, targetDateStr);

      const driverStatus = vehicle.assigned_driver_status;
      const blockedFrom = vehicle.assigned_driver_blocked_from;
      const blockedTo = vehicle.assigned_driver_blocked_to;

      let assignedDriverIsHidden = false;
      if (driverStatus === "I") {
        assignedDriverIsHidden = true;
      } else if (driverStatus === "B") {
        if (blockedFrom && blockedTo) {
          const target = new Date(targetDateStr);
          const from = new Date(blockedFrom);
          const to = new Date(blockedTo);
          target.setHours(0, 0, 0, 0);
          from.setHours(0, 0, 0, 0);
          to.setHours(0, 0, 0, 0);
          assignedDriverIsHidden = target >= from && target <= to;
        } else {
          assignedDriverIsHidden = true;
        }
      } else if (driverStatus !== "A") {
        assignedDriverIsHidden = true;
      }

      const driverOtherVehicleBookings =
        driverBookingsOnOtherVehicles[vehicle.assigned_driver_id] || [];
      const assignedDriverOnDifferentVehicle = driverOtherVehicleBookings.some(
        (entry) => entry.vehicle_id !== vehicle.vehicle_id,
      );
      const shouldHideAssignedDriver =
        assignedDriverIsHidden || assignedDriverOnDifferentVehicle;

      const displayDriverId = shouldHideAssignedDriver
        ? null
        : vehicle.assigned_driver_id;
      const displayDriverName = shouldHideAssignedDriver
        ? null
        : vehicle.assigned_driver_name;
      const displayDriverPhone = shouldHideAssignedDriver
        ? null
        : vehicle.assigned_driver_phone;
      const displayDriverCode = shouldHideAssignedDriver
        ? null
        : vehicle.assigned_driver_code;

      if (bookingsForDate.length > 0) {
        bookingsForDate.forEach((booking) => {
          result.push({
            vehicle_id: vehicle.vehicle_id,
            vehicle_name: vehicle.vehicle_name,
            vehicle_number: vehicle.vehicle_number,
            vehicle_code: vehicle.vehicle_code,
            vehicle_type: vehicle.vehicle_type,
            is_temp: vehicle.is_temp,
            has_booking: true,
            booking: booking,
            bookingsForDate: [booking],
            bookingCount: 1,
            all_bookings: vehicle.bookings,
            is_blocked: blocked,
            blocked_from: vehicle.blocked_from,
            blocked_to: vehicle.blocked_to,
            blocked_reason: vehicle.blocked_reason,
            assigned_driver_id: displayDriverId,
            assigned_driver_name: displayDriverName,
            assigned_driver_phone: displayDriverPhone,
            assigned_driver_code: displayDriverCode,
          });
        });
      } else {
        if (vehicle.is_temp) return;
        if (isPastDate) return;
        result.push({
          vehicle_id: vehicle.vehicle_id,
          vehicle_name: vehicle.vehicle_name,
          vehicle_number: vehicle.vehicle_number,
          vehicle_code: vehicle.vehicle_code,
          vehicle_type: vehicle.vehicle_type,
          is_temp: vehicle.is_temp,
          has_booking: false,
          booking: null,
          bookingsForDate: [],
          bookingCount: 0,
          all_bookings: vehicle.bookings,
          is_blocked: blocked,
          blocked_from: vehicle.blocked_from,
          blocked_to: vehicle.blocked_to,
          blocked_reason: vehicle.blocked_reason,
          assigned_driver_id: displayDriverId,
          assigned_driver_name: displayDriverName,
          assigned_driver_phone: displayDriverPhone,
          assigned_driver_code: displayDriverCode,
        });
      }
    });

    return result;
  }, [vehiclesData, selectedDate, selectedMonth]);

  const filteredVehicles = useMemo(() => {
    if (activeFilter === "AVAILABLE") return [];
    return processedVehicles.filter((vehicle) => {
      if (activeFilter === "ALL") return true;
      // Pending = an actual trip that is still missing a driver or a vehicle.
      // Idle vehicles are NOT pending — they belong under the Available tab.
      if (activeFilter === "PENDING")
        return vehicle.has_booking && vehicle.booking.is_pending;
      if (activeFilter === "CONFIRMED")
        return vehicle.has_booking && !vehicle.booking.is_pending;
      if (activeFilter === "BOOKING")
        return vehicle.has_booking && vehicle.booking.type.includes("Booking");
      if (activeFilter === "ENQUIRY")
        return vehicle.has_booking && vehicle.booking.type.includes("Enquiry");
      return true;
    });
  }, [processedVehicles, activeFilter]);

  const totalTrips = filteredVehicles.reduce(
    (sum, v) => sum + v.bookingCount,
    0,
  );

  const handleEditBtn = () => {
    const bookingToEdit = selectedBooking || selectedVehicle?.booking;
    navigate("/travel/booking", {
      state: {
        booking_id: bookingToEdit.booking_id,
        selectedDate,
        selectedMonth,
      },
    });
  };

  const handleCreateBooking = () => {
    if (onNavigateToBooking) {
      onNavigateToBooking({
        prefilledVehicle: {
          vehicle_id: selectedVehicle.vehicle_id,
          vehicle_name: selectedVehicle.vehicle_name,
          vehicle_number: selectedVehicle.vehicle_number,
          vehicle_code: selectedVehicle.vehicle_code,
        },
      });
    } else {
      navigate("/travel/booking", {
        state: {
          prefilledVehicle: {
            vehicle_id: selectedVehicle.vehicle_id,
            vehicle_name: selectedVehicle.vehicle_name,
            vehicle_number: selectedVehicle.vehicle_number,
            vehicle_code: selectedVehicle.vehicle_code,
          },
          selectedDate,
          selectedMonth,
        },
      });
    }
  };

  const handleExcelExport = () => {
    const allBookings = filteredVehicles.flatMap((v) =>
      v.bookingsForDate.map((booking) => ({
        vehicle_name: v.vehicle_name,
        vehicle_number: v.vehicle_number,
        vehicle_code: v.vehicle_code,
        ...booking,
      })),
    );
    if (allBookings.length === 0) {
      setSnackbar({
        open: true,
        message: "No bookings to export",
        severity: "error",
      });
      return;
    }
    const excelData = allBookings.map((item, index) => ({
      "Sr. No": index + 1,
      "Pickup Date": isoToDDMMYYYY(item.trip_info?.pickup_date) || "N/A",
      "Booking ID": item.booking_id,
      Type: item.type,
      "Customer Name": item.customer_name,
      "Customer Mobile": item.customer_mobile,
      "Vehicle Name": item.vehicle_name,
      "Vehicle Number": item.vehicle_number,
      "Driver Name": item.driver_name,
      "Driver Phone": item.driver_phone || "N/A",
      Amount: item.amount,
      "Trip Mode": item.trip_info?.mode || "N/A",
      "Pickup Time": formatTime(item.trip_info.pickup_time),
      "Drop Date": isoToDDMMYYYY(item.trip_info?.drop_date) || "N/A",
      PAX: item.trip_info?.pax || "N/A",
      "Vehicle Type": item.trip_info?.vehicle_type || "N/A",
      "Payment Details": item.trip_info?.payment_details || "N/A",
      "Trip Details": item.trip_details || "N/A",
      Instructions: item.instructions || "N/A",
      Status: item.is_pending ? "Pending" : "Confirmed",
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const columnWidths = [
      { wch: 8 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 20 },
      { wch: 18 },
      { wch: 20 },
      { wch: 15 },
      { wch: 12 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 18 },
      { wch: 20 },
      { wch: 30 },
      { wch: 30 },
      { wch: 12 },
    ];
    worksheet["!cols"] = columnWidths;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings");
    const [monthStr, yearStr] = selectedMonth.split(" ");
    XLSX.writeFile(
      workbook,
      `Travel_Bookings_${selectedDate}_${monthStr}_${yearStr}.xlsx`,
    );
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("91")) return cleaned;
    if (cleaned.length === 10) return `91${cleaned}`;
    return cleaned;
  };

  const handleOpenWhatsAppDialog = async (
    templateName,
    recipientPhone,
    recipientName,
    vehicleData,
    bookingData,
  ) => {
    setCurrentRecipientName(recipientName);
    setCurrentTemplateName(templateName);
    setCurrentRecipientPhone(formatPhoneNumber(recipientPhone));
    setCurrentBookingId(bookingData?.booking_id);
    setIsWhatsappDialogOpen(true);
    setIsGenerating(true);
    setWaText("");
    const template = templateData?.templates?.find(
      (t) => t.name === templateName,
    );
    if (!template) {
      console.error(`Template ${templateName} not found`);
      setIsGenerating(false);
      return;
    }
    let content = template.content;
    const booking = bookingData;
    if (!booking) {
      console.error("No booking selected");
      setIsGenerating(false);
      return;
    }
    const replacements = {
      "<DATE>": isoToDDMMYYYY(booking.trip_info?.pickup_date) || "N/A",
      "<TIME>": formatTime(booking.trip_info.pickup_time) || "N/A",
      "<VEHTYPE>":
        booking.trip_info?.vehicle_type || vehicleData?.vehicle_name || "N/A",
      "<TRIPDETAILS>": booking.trip_details || "N/A",
      "<BOOKINGAMT>": `₹${booking.amount}` || "N/A",
      "<STORENAME>": userInfo?.store_name || "Our Company",
      "<VEHNO>": vehicleData?.vehicle_number || "N/A",
      "<DRIVERNAME>": booking.driver_name || "N/A",
      "<DRIVERCONTACT>": booking.driver_phone || "N/A",
      "<CUSTNAME>": booking.customer_name || "N/A",
      "<CUSTMOBNO>": booking.customer_mobile || "N/A",
      "<PICKUPADDESS>": booking?.trip_info?.pickup_address || "N/A",
      "<PICKUPLOCATION>": booking.trip_info?.pickup_location || "N/A",
      "<PAYNOTE>": booking.trip_info?.payment_details || "N/A",
    };
    Object.keys(replacements).forEach((key) => {
      content = content.replaceAll(key, replacements[key]);
    });
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setWaText(content);
    setIsGenerating(false);
  };

  const handleSendWA = async () => {
    if (isSendingWA) return;
    try {
      if (!waText) {
        setSnackbar({
          open: true,
          message: "Message is required",
          severity: "error",
        });
        return;
      }
      if (!currentRecipientPhone) {
        setSnackbar({
          open: true,
          message: "Recipient phone number is missing",
          severity: "error",
        });
        return;
      }
      const rawNumbers = currentRecipientPhone
        .split(",")
        .map((n) => n.replace(/\s+/g, ""))
        .filter(Boolean);
      if (rawNumbers.length === 0) {
        setSnackbar({
          open: true,
          message: "No valid phone numbers entered",
          severity: "error",
        });
        return;
      }
      const invalidNumbers = rawNumbers.filter((n) => !/^91\d{10}$/.test(n));
      if (invalidNumbers.length > 0) {
        setSnackbar({
          open: true,
          message: `Invalid number(s): ${invalidNumbers.join(
            ", ",
          )} — each must start with 91 and be 12 digits`,
          severity: "error",
        });
        return;
      }
      const uniqueNumbers = [...new Set(rawNumbers)];
      setIsSendingWA(true);
      const payload = {
        token: templateData.token,
        phone: uniqueNumbers.join(","),
        message: waText,
        store_id: store_id,
        booking_id: currentBookingId,
        template_name: currentTemplateName,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/sendwpmessage`,
        payload,
      );
      if (response.data.success) {
        const results = response.data.data?.results || [];
        const failed = results.filter((r) => !r.success);
        const succeeded = results.filter((r) => r.success);
        if (failed.length === 0) {
          setSnackbar({
            open: true,
            message:
              uniqueNumbers.length === 1
                ? "WhatsApp message sent successfully!"
                : `WhatsApp message sent to all ${succeeded.length} number(s)`,
            severity: "success",
          });
        } else {
          setSnackbar({
            open: true,
            message: `Sent to ${succeeded.length} number(s). Failed: ${failed
              .map((f) => f.phone)
              .join(", ")}`,
            severity: "warning",
          });
        }
        setIsWhatsappDialogOpen(false);
        setWaText("");
        setCurrentTemplateName("");
        setCurrentRecipientPhone("");
        setCurrentBookingId(null);
        await onRefresh();
      } else {
        setSnackbar({
          open: true,
          message:
            response.data.message ||
            response.data.statusMessage ||
            "Failed to send WhatsApp message",
          severity: "error",
        });
      }
    } catch (err) {
      console.error("WhatsApp send error:", err);
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.statusMessage ||
          "Failed to send WhatsApp message",
        severity: "error",
      });
    } finally {
      setIsSendingWA(false);
    }
  };

  const isTemplateSent = (booking, templateName) => {
    if (!booking || !booking.wp_msg_status) return false;
    return (
      Array.isArray(booking.wp_msg_status) &&
      booking.wp_msg_status.includes(templateName)
    );
  };

  // Props object to pass to AssignmentPanel
  const assignmentPanelProps = {
    isVehicleOtherMode,
    tempVehicleAssign,
    setTempVehicleAssign,
    setIsVehicleOtherMode,
    setSelectedVehicleId,
    setAssignVehicleObj,
    availableVehicles,
    assignVehicleObj,
    handleVehicleChange,
    isVehicleBooked,
    isDriverOtherMode,
    tempDriverAssign,
    setTempDriverAssign,
    setIsDriverOtherMode,
    setSelectedDriverId,
    setAssignDriverObj,
    availableDrivers,
    assignDriverObj,
    handleDriverChange,
    isDriverBooked,
    startDate,
    startTime,
    setStartDate,
    setStartTime,
    endDate,
    endTime,
    setEndDate,
    setEndTime,
    handleUpdateAssignment,
    isUpdatingAssignment,
    // Remounts the date pickers when the selection changes, so a half-typed
    // date from the previous row doesn't linger when the new value is "".
    dateFieldKey: `${selectedVehicle?.vehicle_id ?? ""}-${selectedBookingId ?? ""}`,
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
          transition: "margin-right 0.3s ease-in-out",
          marginRight: selectedVehicle ? { xs: 0, md: "320px" } : 0,
        }}
      >
        {/* =================== COMPACT HEADER (exactly like CustomerPaymentDashboard) =================== */}
        <Box
          sx={{
            px: 1.5,
            pt: 0.75,
            pb: 0.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {/* Info row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.6,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {selectedDate} {selectedMonth} •{" "}
              {activeFilter === "AVAILABLE"
                ? `${tabAvailableVehicles.length} available`
                : `${totalTrips} trips`}
            </Typography>

            {/* Export button compact */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "#3873f3",
                px: 1,
                py: 0.4,
                borderRadius: 1.5,
                cursor: "pointer",
              }}
              onClick={handleExcelExport}
            >
              <IosShareIcon sx={{ fontSize: 16, color: "#fff" }} />
            </Box>
          </Box>

          {/* Day strip – compact 26px chips */}
          {/* Day strip — chips reduced from 34px → 26px */}
          <Box sx={{ overflowX: "auto", pb: 0.4 }}>
            <Box sx={{ display: "flex", gap: 0.35, minWidth: "max-content" }}>
              {/* ── LEFT: previous‑month button ── */}
              {extendedDays.length > 0 && extendedDays[0].isPrev && (
                <Box
                  onClick={() => {
                    const prevItem = extendedDays[0];
                    setSelectedMonth(prevItem.label);
                    setSelectedDate(1);
                  }}
                  sx={{
                    minWidth: 26,
                    height: 26,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 0.75,
                    cursor: "pointer",
                    fontSize: "0.72rem",
                    fontWeight: 400,
                    bgcolor: "grey.50",
                    color: "text.disabled",
                    border: "1px dashed #ccc",
                    px: 1,
                    "&:hover": {
                      bgcolor: "grey.200",
                      color: "text.primary",
                    },
                  }}
                >
                  {extendedDays[0].label.split(" ")[0]}
                </Box>
              )}

              {/* ── CURRENT MONTH DAYS ── */}
              {extendedDays
                .filter((item) => item.isCurrentMonth)
                .map((item, idx) => {
                  const isSelected = selectedDate === item.day;
                  return (
                    <Box
                      key={idx}
                      onClick={() => setSelectedDate(item.day)}
                      sx={{
                        minWidth: 26,
                        height: 26,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 0.75,
                        cursor: "pointer",
                        fontSize: "0.72rem",
                        fontWeight: isSelected ? 600 : 400,
                        bgcolor: isSelected ? "primary.main" : "grey.100",
                        color: isSelected ? "white" : "text.primary",
                        border: "none",
                        "&:hover": {
                          bgcolor: isSelected ? "primary.dark" : "grey.200",
                          color: isSelected ? "white" : "text.primary",
                        },
                      }}
                    >
                      {item.day}
                    </Box>
                  );
                })}

              {/* ── RIGHT: next‑month button ── */}
              {extendedDays.length > 0 &&
                extendedDays[extendedDays.length - 1].isPrev === false &&
                !extendedDays[extendedDays.length - 1].isCurrentMonth && (
                  <Box
                    onClick={() => {
                      const nextItem = extendedDays[extendedDays.length - 1];
                      setSelectedMonth(nextItem.label);
                      setSelectedDate(1);
                    }}
                    sx={{
                      minWidth: 26,
                      height: 26,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 0.75,
                      cursor: "pointer",
                      fontSize: "0.72rem",
                      fontWeight: 400,
                      bgcolor: "grey.50",
                      color: "text.disabled",
                      border: "1px dashed #ccc",
                      px: 1,
                      "&:hover": {
                        bgcolor: "grey.200",
                        color: "text.primary",
                      },
                    }}
                  >
                    {extendedDays[extendedDays.length - 1].label.split(" ")[0]}
                  </Box>
                )}
            </Box>
          </Box>
        </Box>

        {/* =================== TABLE =================== */}
        <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
          {activeFilter === "AVAILABLE" ? (
            <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  {[
                    ["Code", "80px"],
                    ["Vehicle Name", "180px"],
                    ["Number", "130px"],
                    ["Type", "120px"],
                    ["Status", "90px"],
                  ].map(([label, width]) => (
                    <TableCell
                      key={label}
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.85rem" },
                        py: 0.75,
                        px: 1,
                        width,
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetchingTabAvailable ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : tabAvailableVehicles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.8rem", md: "0.9rem" } }}
                      >
                        No vehicles available for {selectedDate} {selectedMonth}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tabAvailableVehicles.map((v) => (
                    <TableRow
                      key={v.vehicle_id}
                      hover
                      sx={{ cursor: "default" }}
                    >
                      <TableCell sx={{ py: 0.6, px: 1 }}>
                        <Typography
                          sx={{
                            fontSize: { xs: "0.75rem", md: "0.85rem" },
                            fontWeight: 500,
                          }}
                        >
                          {v.vehicle_code}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.6, px: 1 }}>
                        <Typography
                          sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" } }}
                        >
                          {v.vehicle_name || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.6, px: 1 }}>
                        <Typography
                          sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" } }}
                        >
                          {v.vehicle_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.6, px: 1 }}>
                        <Typography
                          sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" } }}
                        >
                          {v.vehicle_type || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.6, px: 1 }}>
                        <Chip
                          label="Available"
                          color="success"
                          size="small"
                          sx={{ fontSize: "0.7rem", height: 18 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          ) : (
            <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  {[
                    ["Vehicle", "40px"],
                    ["Driver", "40px"],
                    ["Start", "60px"],
                    ["End", "60px"],
                    ["Veh Type", "40px"],
                    ["Customer", "80px"],
                    ["Trip Details", "160px"],
                    ["Payment", "60px"],
                    ["Type", "60px"],
                    ["WhatsApp", "60px"],
                  ].map(([label, width]) => (
                    <TableCell
                      key={label}
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", md: "0.85rem" },
                        py: 0.75,
                        px: 1,
                        width,
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVehicles.map((v, index) => {
                  const bookingId = v.booking?.booking_id || `row-${index}`;
                  const tripDetails = v.has_booking
                    ? v.booking.trip_details || "-"
                    : "-";
                  const isExpanded = expandedTripRows[bookingId] || false;
                  const showToggle = tripDetails.length > 50;

                  return (
                    <TableRow
                      key={`${v.vehicle_id}-${v.booking?.booking_id || index}`}
                      hover
                      onClick={() => handleRowClick(v)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedVehicle?.vehicle_id === v.vehicle_id &&
                          selectedVehicle?.booking?.booking_id ===
                            v.booking?.booking_id
                            ? "action.selected"
                            : v.is_blocked
                            ? "#ffebee"
                            : "inherit",
                      }}
                    >
                      {/* Vehicle */}
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          py: 0.6,
                          px: 1,
                          width: "40px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: "0.75rem", md: "0.85rem" },
                            fontWeight: 500,
                          }}
                        >
                          {v.vehicle_code}
                        </Typography>
                      </TableCell>

                      {/* Driver */}
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          py: 0.6,
                          px: 1,
                          width: "40px",
                        }}
                      >
                        {v.has_booking ? (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: { xs: "0.75rem", md: "0.85rem" },
                                fontWeight: 500,
                              }}
                            >
                              {v.booking.driver_code === "Not Assigned"
                                ? "N/A"
                                : v.booking.driver_code}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: { xs: "0.65rem", md: "0.7rem" },
                                color: "text.secondary",
                              }}
                            >
                              {v.booking.driver_phone}
                            </Typography>
                          </Box>
                        ) : v.assigned_driver_code ? (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: { xs: "0.75rem", md: "0.85rem" },
                                fontWeight: 500,
                                color: "text.secondary",
                              }}
                            >
                              {v.assigned_driver_code}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: { xs: "0.65rem", md: "0.7rem" },
                                color: "text.disabled",
                              }}
                            >
                              {v.assigned_driver_phone}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: { xs: "0.75rem", md: "0.85rem" },
                              color: "text.disabled",
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Start Date & Time */}
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          py: 0.6,
                          px: 1,
                          width: "120px",
                        }}
                      >
                        {v.has_booking ? (
                          <Typography
                            sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                          >
                            {formatDateTime24(
                              v.booking.trip_info?.pickup_date,
                              v.booking.trip_info.pickup_time,
                            )}
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: { xs: "0.75rem", md: "0.85rem" },
                              color: "text.disabled",
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* End Date & Time */}
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          py: 0.6,
                          px: 1,
                          width: "120px",
                        }}
                      >
                        {v.has_booking && v.booking.trip_info?.drop_date ? (
                          <Typography
                            sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                          >
                            {v.booking.trip_info?.drop_time
                              ? formatDateTime24(
                                  v.booking.trip_info?.drop_date,
                                  v.booking.trip_info?.drop_time,
                                )
                              : (() => {
                                  const [y, m, d] =
                                    v.booking.trip_info.drop_date.split("-");
                                  return `${d}-${m}`;
                                })()}
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: { xs: "0.75rem", md: "0.85rem" },
                              color: "text.disabled",
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Vehicle Type */}
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          py: 0.6,
                          px: 1,
                          width: "40px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: "0.75rem", md: "0.85rem" },
                            fontWeight: 400,
                          }}
                        >
                          {v.has_booking
                            ? v.booking.trip_info?.vehicle_type
                            : v.vehicle_type}
                        </Typography>
                      </TableCell>

                      {/* Customer */}
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          py: 0.6,
                          px: 1,
                          width: "80px",
                        }}
                      >
                        {v.has_booking ? (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: { xs: "0.75rem", md: "0.85rem" },
                                fontWeight: 500,
                                color: "primary.main",
                              }}
                            >
                              {v.booking.customer_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: { xs: "0.65rem", md: "0.7rem" },
                                color: "text.secondary",
                              }}
                            >
                              {v.booking.customer_mobile}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: { xs: "0.75rem", md: "0.85rem" },
                              color: "text.disabled",
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>

                      {/* Trip Details with smooth expand/collapse */}
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          py: 0.6,
                          px: 1,
                          width: "180px",
                          maxWidth: "180px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              maxHeight: isExpanded ? "500px" : "4em",
                              overflow: "hidden",
                              transition: "max-height 0.4s ease",
                              flex: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: { xs: "0.75rem", md: "0.85rem" },
                                lineHeight: 1.3,
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {tripDetails}
                            </Typography>
                          </Box>
                          {showToggle && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleTripExpand(bookingId);
                              }}
                              sx={{ p: 0, flexShrink: 0, mt: 0.3 }}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>

                      {/* Payment Details (80px, single line) */}
                      <TableCell
                        sx={{
                          fontSize: { xs: "0.75rem", md: "0.85rem" },
                          py: 0.6,
                          px: 1,
                          width: "80px",
                          maxWidth: "80px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: "0.75rem", md: "0.85rem" },
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.4,
                          }}
                        >
                          {v.has_booking
                            ? v.booking.trip_info?.payment_details || "-"
                            : "-"}
                        </Typography>
                      </TableCell>

                      {/* Type */}
                      <TableCell sx={{ py: 0.6, px: 1, width: "60px" }}>
                        {v.has_booking ? (
                          <Chip
                            size="small"
                            label={v.booking.type}
                            color={v.booking.is_pending ? "warning" : "success"}
                            sx={{
                              fontSize: { xs: "0.65rem", md: "0.7rem" },
                              height: 18,
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label={v.is_blocked ? "Blocked" : "Available"}
                            color={v.is_blocked ? "error" : "default"}
                            sx={{
                              fontSize: { xs: "0.65rem", md: "0.7rem" },
                              height: 18,
                            }}
                          />
                        )}
                      </TableCell>

                      {/* WhatsApp */}
                      <TableCell sx={{ py: 0.6, px: 1, width: "60px" }}>
                        {v.has_booking ? (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            <WhatsAppIcon
                              sx={{ fontSize: 16, color: "#00b62d" }}
                            />
                            {["BC", "DD", "BD"].map((tpl) => (
                              <Box
                                key={tpl}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenWhatsAppDialog(
                                    tpl,
                                    tpl === "BD"
                                      ? v.booking.driver_phone
                                      : v.booking.customer_mobile,
                                    tpl === "BD"
                                      ? v.booking.driver_name
                                      : v.booking.customer_name,
                                    v,
                                    v.booking,
                                  );
                                }}
                                sx={{
                                  bgcolor: isTemplateSent(v.booking, tpl)
                                    ? "#00b62d"
                                    : "#d6ffe5",
                                  border: "1px solid #00b62d",
                                  color: isTemplateSent(v.booking, tpl)
                                    ? "#FFF"
                                    : "#00b62d",
                                  borderRadius: "4px",
                                  px: 0.8,
                                  py: 0.2,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  fontSize: "0.65rem",
                                  position: "relative",
                                  "&:hover": {
                                    bgcolor: "#1ebe5d",
                                    color: "#FFF",
                                  },
                                }}
                              >
                                {tpl}
                                {isTemplateSent(v.booking, tpl) && (
                                  <CheckCircleIcon
                                    sx={{
                                      fontSize: 10,
                                      position: "absolute",
                                      top: -4,
                                      right: -4,
                                      color: "#FFF",
                                      bgcolor: "#00b62d",
                                      borderRadius: "50%",
                                    }}
                                  />
                                )}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: { xs: "0.75rem", md: "0.85rem" },
                              color: "text.disabled",
                            }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredVehicles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                      <Typography
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.8rem", md: "0.9rem" } }}
                      >
                        No trip/vehicle found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>

      {/* Right Panel (unchanged) */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: { xs: "100%", md: "360px" },
          height: "100%",
          borderLeft: 1,
          borderColor: "divider",
          p: { xs: 1, md: 1.5 },
          overflow: "auto",
          bgcolor: "background.paper",
          transform: selectedVehicle ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s ease-in-out",
          boxShadow: selectedVehicle ? { xs: 3, md: 2 } : 0,
          zIndex: 10,
        }}
      >
        {selectedVehicle ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: { xs: "1rem", md: "1.15rem" } }}
              >
                {selectedVehicle.has_booking
                  ? selectedVehicle.bookingCount > 1
                    ? `${selectedVehicle.bookingCount} Bookings`
                    : "Booking Details"
                  : "Vehicle Details"}
              </Typography>
              <IconButton
                onClick={() => {
                  setSelectedVehicle(null);
                  setSelectedBookingId(null);
                  resetAssignmentFields();
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Card variant="outlined" sx={{ mb: 1.2 }}>
              {selectedVehicle.vehicle_name !== "Not Assigned" && (
                <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={0.3}
                    sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                  >
                    Vehicle
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.9rem", md: "0.95rem" } }}
                  >
                    {selectedVehicle.vehicle_code} -{" "}
                    {selectedVehicle.vehicle_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                  >
                    {selectedVehicle.vehicle_number}
                  </Typography>
                  {selectedVehicle.is_blocked && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label="BLOCKED"
                        color="error"
                        size="small"
                        sx={{ fontSize: "0.65rem", fontWeight: 600 }}
                      />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          fontSize: "0.7rem",
                          color: "error.main",
                          mt: 0.5,
                        }}
                      >
                        {isoToDDMMYYYY(selectedVehicle.blocked_from)} to{" "}
                        {isoToDDMMYYYY(selectedVehicle.blocked_to)}
                      </Typography>
                      {selectedVehicle.blocked_reason && (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ fontSize: "0.7rem", mt: 0.3 }}
                        >
                          Reason: {selectedVehicle.blocked_reason}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              )}
            </Card>

            {selectedVehicle.has_booking ? (
              selectedVehicle.bookingCount > 1 ? (
                <>
                  <Alert severity="info" sx={{ mb: 1.2, fontSize: "0.8rem" }}>
                    This vehicle has {selectedVehicle.bookingCount} bookings on
                    this date. Select one to view details.
                  </Alert>
                  <List dense sx={{ mb: 1.2 }}>
                    {selectedVehicle.bookingsForDate.map((booking, index) => (
                      <React.Fragment key={booking.booking_id}>
                        <ListItem
                          button
                          selected={selectedBookingId === booking.booking_id}
                          onClick={() =>
                            handleBookingSelect(booking.booking_id)
                          }
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            bgcolor:
                              selectedBookingId === booking.booking_id
                                ? "action.selected"
                                : "transparent",
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: { xs: "0.8rem", md: "0.85rem" },
                                  fontWeight: 500,
                                }}
                              >
                                {formatTime(booking.trip_info.pickup_time)} -{" "}
                                {booking.customer_name}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                sx={{
                                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                                }}
                              >
                                {booking.booking_id}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < selectedVehicle.bookingsForDate.length - 1 && (
                          <Divider sx={{ my: 0.5 }} />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                  {selectedBooking && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.2,
                      }}
                    >
                      <AssignmentPanel {...assignmentPanelProps} />
                      <Card variant="outlined">
                        <CardContent
                          sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.3}
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          >
                            Booking ID
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ fontSize: { xs: "0.85rem", md: "0.9rem" } }}
                          >
                            {selectedBooking.booking_id}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card variant="outlined">
                        <CardContent
                          sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.3}
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          >
                            Customer
                          </Typography>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
                          >
                            {selectedBooking.customer_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                          >
                            📱 {selectedBooking.customer_mobile}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card variant="outlined">
                        <CardContent
                          sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          >
                            Driver
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                          >
                            {selectedBooking.driver_name}
                          </Typography>
                          {selectedBooking.driver_phone && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                            >
                              {selectedBooking.driver_phone}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                      <Card variant="outlined">
                        <CardContent
                          sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.8}
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          >
                            Trip Info
                          </Typography>
                          <Box sx={{ mb: 0.8 }}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                            >
                              ⏰{" "}
                              {formatTime(
                                selectedBooking.trip_info.pickup_time,
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: { xs: "0.75rem", md: "0.8rem" } }}
                            >
                              Mode: {selectedBooking.trip_info?.mode || "N/A"}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: { xs: "0.75rem", md: "0.8rem" } }}
                            >
                              Passengers:{" "}
                              {selectedBooking.trip_info?.pax || "N/A"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                            >
                              Pickup:{" "}
                              {isoToDDMMYYYY(
                                selectedBooking.trip_info?.pickup_date,
                              )}
                            </Typography>
                            <br />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                            >
                              Drop:{" "}
                              {isoToDDMMYYYY(
                                selectedBooking.trip_info?.drop_date,
                              )}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                      <Card variant="outlined">
                        <CardContent
                          sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          >
                            Status
                          </Typography>
                          <Chip
                            label={selectedBooking.type}
                            size="small"
                            color={
                              selectedBooking.is_pending ? "warning" : "success"
                            }
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          />
                        </CardContent>
                      </Card>
                      {selectedBooking.trip_info.payment_details && (
                        <Card variant="outlined">
                          <CardContent
                            sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.8}
                              sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                            >
                              Payment Details
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                            >
                              {selectedBooking.trip_info.payment_details}
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                      {selectedBooking.trip_details && (
                        <Card variant="outlined">
                          <CardContent
                            sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.8}
                              sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                            >
                              Trip Details
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                            >
                              {selectedBooking.trip_details}
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                      {selectedBooking.instructions && (
                        <Card variant="outlined">
                          <CardContent
                            sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.8}
                              sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                            >
                              Instructions
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                            >
                              {selectedBooking.instructions}
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                      <Button
                        onClick={() =>
                          navigate("/travel/booking", {
                            state: {
                              booking_id: selectedBooking.booking_id,
                              selectedDate,
                              selectedMonth,
                            },
                          })
                        }
                        variant="contained"
                        startIcon={<EditIcon />}
                        fullWidth
                        sx={{ fontSize: "0.8rem" }}
                      >
                        Edit Full Booking
                      </Button>
                      <Button
                        onClick={(e) => handleDeleteClick(e, selectedBooking)}
                        variant="outlined"
                        color="error"
                        fullWidth
                        sx={{ fontSize: "0.8rem" }}
                      >
                        Cancel Booking
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}
                >
                  <AssignmentPanel {...assignmentPanelProps} />
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button
                        onClick={handleEditBtn}
                        variant="outlined"
                        startIcon={<EditIcon />}
                        fullWidth
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Edit
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        onClick={(e) =>
                          handleDeleteClick(e, selectedVehicle.booking)
                        }
                        variant="outlined"
                        color="error"
                        fullWidth
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.3}
                        sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                      >
                        Booking ID
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: "0.85rem", md: "0.9rem" } }}
                      >
                        {selectedVehicle.booking.booking_id}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.3}
                        sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                      >
                        Customer
                      </Typography>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
                      >
                        {selectedVehicle.booking.customer_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                      >
                        📱 {selectedVehicle.booking.customer_mobile}
                      </Typography>
                    </CardContent>
                  </Card>
                  {selectedVehicle.booking.driver_name !== "Not Assigned" && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          Driver
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                        >
                          {selectedVehicle.booking.driver_name}
                        </Typography>
                        {selectedVehicle.booking.driver_phone && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          >
                            {selectedVehicle.booking.driver_phone}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.8}
                        sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                      >
                        Trip Info
                      </Typography>
                      <Box sx={{ mb: 0.8 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                        >
                          ⏰{" "}
                          {formatTime(
                            selectedVehicle.booking.trip_info.pickup_time,
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.75rem", md: "0.8rem" } }}
                        >
                          Mode:{" "}
                          {selectedVehicle.booking.trip_info?.mode || "N/A"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.75rem", md: "0.8rem" } }}
                        >
                          Passengers:{" "}
                          {selectedVehicle.booking.trip_info?.pax || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          Pickup:{" "}
                          {isoToDDMMYYYY(
                            selectedVehicle.booking.trip_info?.pickup_date,
                          )}
                        </Typography>
                        <br />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          Drop:{" "}
                          {isoToDDMMYYYY(
                            selectedVehicle.booking.trip_info?.drop_date,
                          )}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.5}
                        sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                      >
                        Status
                      </Typography>
                      <Chip
                        label={selectedVehicle.booking.type}
                        size="small"
                        color={
                          selectedVehicle.booking.is_pending
                            ? "warning"
                            : "success"
                        }
                        sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                      />
                    </CardContent>
                  </Card>
                  {selectedVehicle.booking.trip_info?.payment_details && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.8}
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          Payment Details
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                        >
                          {selectedVehicle.booking.trip_info.payment_details}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                  {selectedVehicle.booking.trip_details && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.8}
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          Trip Details
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                        >
                          {selectedVehicle.booking.trip_details}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                  {selectedVehicle.booking.instructions && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.8}
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          Instructions
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                        >
                          {selectedVehicle.booking.instructions}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
                  This vehicle is currently{" "}
                  {selectedVehicle.is_blocked ? "blocked" : "available"} for{" "}
                  {selectedDate} {selectedMonth}
                </Alert>
                {!selectedVehicle.is_blocked && (
                  <Button
                    onClick={handleCreateBooking}
                    variant="contained"
                    startIcon={<AddIcon />}
                    fullWidth
                    sx={{ fontSize: "0.8rem" }}
                  >
                    Create Booking
                  </Button>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: { xs: "0.8rem", md: "0.9rem" } }}
            >
              Select a row to view details
            </Typography>
          </Box>
        )}
      </Box>

      {/* Cancel Trip Dialog (unchanged) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this booking?
            {bookingToDelete && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Booking ID: {bookingToDelete.booking_id}
                </Typography>
                <Typography variant="body2">
                  Customer: {bookingToDelete.customer_name}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- IMPROVED Conflict Confirmation Dialog (unchanged) ---- */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogNo}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "#fff3e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: "1rem" }}>⚠️</Typography>
          </Box>
          <Typography fontWeight={700} sx={{ fontSize: "1rem" }}>
            {pendingSelectionType === "driver" ? "Driver" : "Vehicle"} Already
            Booked
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, fontSize: "0.85rem" }}
          >
            This {pendingSelectionType === "driver" ? "driver" : "vehicle"} has
            a conflicting booking:
          </Typography>
          <Box
            sx={{
              bgcolor: "#fff8e1",
              border: "1px solid #ffe082",
              borderRadius: 1.5,
              p: 1.5,
              mb: 1.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: "0.82rem", color: "#5d4037", fontWeight: 500 }}
            >
              {conflictMessage}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            Do you still want to assign this{" "}
            {pendingSelectionType === "driver" ? "driver" : "vehicle"}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            onClick={handleConfirmDialogNo}
            variant="outlined"
            size="small"
            sx={{ fontSize: "0.8rem" }}
          >
            No, go back
          </Button>
          <Button
            onClick={handleConfirmDialogYes}
            variant="contained"
            color="warning"
            size="small"
            sx={{ fontSize: "0.8rem" }}
          >
            Yes, assign anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Dialog (unchanged) */}
      <Dialog
        open={isWhatsappDialogOpen}
        onClose={() => {
          setIsWhatsappDialogOpen(false);
          setWaText("");
          setIsGenerating(false);
          setCurrentRecipientName("");
          setCurrentRecipientPhone("");
          setCurrentTemplateName("");
          setCurrentBookingId(null);
        }}
        PaperProps={{
          sx: {
            width: "50%",
            bgcolor: "#0B141A",
            color: "white",
            borderRadius: 3,
            backdropFilter: "blur(10px)",
          },
        }}
        sx={{ "& .MuiBackdrop-root": { backdropFilter: "blur(4px)" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            bgcolor: "#075E54",
            color: "white",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            gap: 1.5,
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 15 }}>
              {currentRecipientName}
            </Typography>
            <TextField
              value={currentRecipientPhone}
              onChange={(e) => setCurrentRecipientPhone(e.target.value)}
              variant="standard"
              size="small"
              placeholder="91XXXXXXXXXX or 91XXXXXXXXXX, 91XXXXXXXXXX"
              multiline
              sx={{
                mt: 0.5,
                width: "100%",
                "& .MuiInputBase-input": {
                  color: "white",
                  fontSize: "0.85rem",
                  padding: "2px 0",
                },
                "& .MuiInputBase-inputMultiline": {
                  color: "white",
                  fontSize: "0.85rem",
                },
                "& .MuiInput-underline:before": {
                  borderBottomColor: "rgba(255,255,255,0.3)",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottomColor: "rgba(255,255,255,0.5)",
                },
                "& .MuiInput-underline:after": { borderBottomColor: "white" },
              }}
            />
            {currentRecipientPhone &&
              (() => {
                const nums = currentRecipientPhone
                  .split(",")
                  .map((n) => n.replace(/\s+/g, ""))
                  .filter(Boolean);
                const invalid = nums.filter((n) => n && !/^91\d{10}$/.test(n));
                if (invalid.length > 0) {
                  return (
                    <Typography
                      sx={{ fontSize: "0.7rem", color: "#ff6b6b", mt: 0.5 }}
                    >
                      ⚠ Invalid: {invalid.join(", ")}
                    </Typography>
                  );
                }
                if (nums.length > 1) {
                  return (
                    <Typography
                      sx={{ fontSize: "0.7rem", color: "#a8e6cf", mt: 0.5 }}
                    >
                      ✓ {nums.length} numbers ready to send
                    </Typography>
                  );
                }
                return null;
              })()}
            <Typography sx={{ fontSize: 12, mt: 0.5, opacity: 0.8 }}>
              {currentTemplateName === "BD" ? "Driver" : "Customer"} •
              comma-separate for multiple
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={15}
            placeholder={
              isGenerating
                ? "Generating WhatsApp text..."
                : "Type your WhatsApp message..."
            }
            value={waText}
            onChange={(e) => setWaText(e.target.value)}
            disabled={isGenerating}
            sx={{
              bgcolor: "#1F2C33",
              borderRadius: 2,
              "& .MuiInputBase-input": {
                color: "white",
                background: isGenerating
                  ? "linear-gradient(90deg, #d8d8d8, #ffffff, #888888)"
                  : "none",
                backgroundSize: "300% 100%",
                WebkitBackgroundClip: isGenerating ? "text" : "initial",
                WebkitTextFillColor: isGenerating ? "transparent" : "white",
                animation: isGenerating
                  ? "shimmer 2s linear infinite, breathe 1.6s ease-in-out infinite"
                  : "none",
                caretColor: isGenerating ? "#838383" : "white",
                transition: "all 0.3s ease",
              },
              "& .MuiInputBase-input::placeholder": {
                color: isGenerating ? "#cfefff" : "#9aa4aa",
                opacity: 1,
              },
              "@keyframes shimmer": {
                "0%": { backgroundPosition: "0% 50%" },
                "100%": { backgroundPosition: "300% 50%" },
              },
              "@keyframes breathe": {
                "0%, 100%": { opacity: 0.85 },
                "50%": { opacity: 1 },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setIsWhatsappDialogOpen(false);
              setWaText("");
              setIsGenerating(false);
              setCurrentRecipientName("");
              setCurrentRecipientPhone("");
              setCurrentTemplateName("");
              setCurrentBookingId(null);
            }}
            sx={{
              color: "white",
              border: "1px solid #555",
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendWA}
            disabled={isSendingWA || isGenerating}
            sx={{
              bgcolor: "#25D366",
              color: "white",
              px: 4,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { bgcolor: "#1EBE5A" },
              "&.Mui-disabled": { bgcolor: "#1EBE5A", opacity: 0.7 },
            }}
          >
            {isSendingWA
              ? "Sending..."
              : (() => {
                  const nums = currentRecipientPhone
                    .split(",")
                    .map((n) => n.replace(/\s+/g, ""))
                    .filter(Boolean);
                  return nums.length > 1 ? `Send to ${nums.length}` : "Send";
                })()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
